import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/apiError';
import { auditLogsApi } from './auditLogsApi';
import { parseChanges, type AuditLogDetail, type EntityChange } from './auditLogTypes';

export interface AuditLogEntryState {
  entry: AuditLogDetail | null;
  /** The parsed `changes` payload — one record per entity the request mutated. */
  changes: EntityChange[];
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

/**
 * Loads one audit entry from `GET /api/audit-logs/{id}`.
 *
 * Shared by the detail modal (§13) and the profile page so the fetch, the abort on
 * unmount, the error normalization, and the defensive `changes` parse exist once. A
 * second copy of this would be the place the two surfaces started disagreeing.
 *
 * `id` may be null — the modal is closed, or the route carried something that is not an
 * entry id. Nothing is requested in that case.
 */
export function useAuditLogEntry(id: number | null): AuditLogEntryState {
  const [entry, setEntry] = useState<AuditLogDetail | null>(null);
  const [loading, setLoading] = useState(id !== null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (id === null) {
      setEntry(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setEntry(null);

    auditLogsApi
      .get(id, controller.signal)
      .then(setEntry)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the entry.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  const changes = useMemo(() => parseChanges(entry?.changes ?? null), [entry]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { entry, changes, loading, error, reload };
}
